import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PlusIcon from './icons/PlusIcon';
import MinusIcon from './icons/MinusIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import { getTranslation, isRTL } from '../utils/translations';
import { isNumberlessUnit, unifyUnit, translateUnitLabel } from '../utils/unitConversion';
import { DISPLAY_FRACTIONS, formatQuantityVulgarDisplay, parseQuantityInput, snapToDisplayFraction } from '../utils/fractionQuantity';
import CookingQuantityLabel from './CookingQuantityLabel';
import type { Language } from '../types';

interface SmartUnitCapsuleProps {
    value: number;
    unit: string;
    availableUnits?: string[];
    onChange: (value: number, unit: string) => void;
    language?: Language;
    step?: number;
    min?: number;
    max?: number;
    isMeat?: boolean;
    bgClass?: string;
    zIndex?: number;
    hideUnit?: boolean;
    /** Show 1/4…3/4 shortcut chips (ingredients only). */
    showFractionShortcuts?: boolean;
    /** Snap +/- to display fractions; use view-style closed label. */
    allowFractions?: boolean;
    /** Render open editor as a fixed popover centered on the trigger. */
    floatWhenOpen?: boolean;
}

const STANDARD_UNITS = ['kg', 'g', 'unit', 'ml', 'liter', 'tsp', 'tbsp', 'cup', 'container', 'tub', 'can', 'bottle', 'bag', 'box', 'roll', 'to taste', 'pinch', 'bunch', 'package', 'clove', 'head', 'stalk', 'shot', 'a_little', 'whole', 'half', 'quarter', 'third', 'to_choose'];
/** Portaled popovers must sit above full-screen modals (e.g. AddRecipeModal z-[200]). */
const MIN_FLOATING_Z_INDEX = 300;

const formatEditorDisplay = (amount: number): string => String(parseFloat(amount.toFixed(2)));

const SmartUnitCapsule: React.FC<SmartUnitCapsuleProps> = ({
    value,
    unit,
    onChange,
    availableUnits = STANDARD_UNITS,
    language,
    step = 0.5,
    min = 0,
    max = 999,
    isMeat = false,
    zIndex = 50,
    hideUnit = false,
    bgClass,
    showFractionShortcuts = false,
    allowFractions = false,
    floatWhenOpen = false,
}) => {
    const t = (k: Parameters<typeof getTranslation>[1]) => getTranslation(language, k);
    const rtl = isRTL(language);
    const [isOpen, setIsOpen] = useState(false);
    const [listenerReady, setListenerReady] = useState(false);
    const [inputText, setInputText] = useState('');
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
    const inputRef = useRef<HTMLInputElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const normalizeValue = useCallback((amount: number) => {
        const clamped = Math.max(min, Math.min(max, amount));
        if (!allowFractions && step >= 1) return Math.round(clamped);
        return snapToDisplayFraction(clamped);
    }, [allowFractions, min, max, step]);

    const handleUpdateValue = useCallback((newVal: number) => {
        const safeVal = normalizeValue(newVal);
        setInputText(formatEditorDisplay(safeVal));
        onChange(safeVal, unit);
    }, [allowFractions, normalizeValue, onChange, unit]);

    const commitInputText = useCallback(() => {
        const parsed = parseQuantityInput(inputText);
        if (parsed === null) {
            setInputText(formatEditorDisplay(value));
            return;
        }
        handleUpdateValue(parsed);
    }, [handleUpdateValue, inputText, value]);

    const closeEditor = useCallback(() => {
        commitInputText();
        setIsOpen(false);
    }, [commitInputText]);

    /** Prevent the closing tap from falling through to the row underneath (e.g. mark item done). */
    const closeEditorWithoutClickThrough = useCallback(() => {
        closeEditor();
        const blockClick = (event: Event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        document.addEventListener('click', blockClick, true);
        window.setTimeout(() => {
            document.removeEventListener('click', blockClick, true);
        }, 400);
    }, [closeEditor]);

    const selectAllInput = useCallback(() => {
        requestAnimationFrame(() => {
            const input = inputRef.current;
            if (!input) return;
            input.focus();
            input.select();
        });
    }, []);

    const floatingZIndex = floatWhenOpen ? Math.max(zIndex + 10, MIN_FLOATING_Z_INDEX) : zIndex + 1;

    const updatePopoverPosition = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        const rect = anchor.getBoundingClientRect();
        const editorWidth = editorRef.current?.offsetWidth ?? 220;
        const centered = rect.left + (rect.width - editorWidth) / 2;
        const maxLeft = window.innerWidth - editorWidth - 8;
        const left = Math.min(Math.max(8, centered), maxLeft);
        setPopoverStyle({
            position: 'fixed',
            top: rect.bottom + 6,
            left,
            zIndex: floatingZIndex,
        });
    }, [floatingZIndex]);

    const openEditor = useCallback(() => {
        const anchor = anchorRef.current;
        if (floatWhenOpen && anchor) {
            const rect = anchor.getBoundingClientRect();
            const editorWidth = 220;
            const centered = rect.left + (rect.width - editorWidth) / 2;
            const maxLeft = window.innerWidth - editorWidth - 8;
            const left = Math.min(Math.max(8, centered), maxLeft);
            setPopoverStyle({
                position: 'fixed',
                top: rect.bottom + 6,
                left,
                zIndex: floatingZIndex,
            });
        }
        setIsOpen(true);
    }, [floatWhenOpen, floatingZIndex]);

    const handleStepButtonPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleStepChange = (e: React.MouseEvent<HTMLButtonElement>, nextValue: number) => {
        e.preventDefault();
        e.stopPropagation();
        handleUpdateValue(nextValue);
        if (floatWhenOpen && isOpen) {
            requestAnimationFrame(updatePopoverPosition);
        }
    };

    const handleFractionChange = (e: React.MouseEvent<HTMLButtonElement>, nextValue: number) => {
        e.preventDefault();
        e.stopPropagation();
        handleUpdateValue(nextValue);
        selectAllInput();
    };

    useEffect(() => {
        if (!isOpen) {
            setListenerReady(false);
            return;
        }
        setInputText(formatEditorDisplay(value));
        const focusTimer = window.setTimeout(() => selectAllInput(), 10);
        const listenerTimer = window.setTimeout(() => setListenerReady(true), 120);
        return () => {
            window.clearTimeout(focusTimer);
            window.clearTimeout(listenerTimer);
            setListenerReady(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-init on open
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen || !floatWhenOpen) return;
        updatePopoverPosition();
    }, [isOpen, floatWhenOpen, updatePopoverPosition, value, showFractionShortcuts]);

    useEffect(() => {
        if (!isOpen || !floatWhenOpen) return;
        window.addEventListener('resize', updatePopoverPosition);
        window.addEventListener('scroll', updatePopoverPosition, true);
        return () => {
            window.removeEventListener('resize', updatePopoverPosition);
            window.removeEventListener('scroll', updatePopoverPosition, true);
        };
    }, [isOpen, floatWhenOpen, updatePopoverPosition]);

    useEffect(() => {
        if (!isOpen || !listenerReady) return;

        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node;
            if (editorRef.current?.contains(target)) return;
            if (anchorRef.current?.contains(target)) return;
            closeEditorWithoutClickThrough();
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [isOpen, listenerReady, closeEditorWithoutClickThrough]);

    const handleUpdateUnit = (newUnit: string) => {
        onChange(isNumberlessUnit(newUnit) ? 1 : value, newUnit);
    };

    const hideQuantity = isNumberlessUnit(unit);
    const canonicalUnit = unit ? unifyUnit(unit) : '';

    const effectiveUnits = useMemo(() => {
        if (!unit) return availableUnits;
        if (availableUnits.some(u => unifyUnit(u) === canonicalUnit)) {
            return availableUnits;
        }
        return [canonicalUnit, ...availableUnits];
    }, [unit, availableUnits, canonicalUnit]);

    const showUnitSelector = effectiveUnits.length > 1;

    const activeBorderColor = isMeat ? 'border-rose-300 dark:border-rose-800' : 'border-brand-sage dark:border-brand-sage/70';
    const btnColor = isMeat ? 'text-rose-600 dark:text-rose-400' : 'text-brand-sage dark:text-brand-sage';
    const ringColor = isMeat ? 'ring-rose-50 dark:ring-rose-900/20' : 'ring-brand-sage/10';

    const triggerClassName = `
        flex items-center justify-center gap-1.5 transition-all font-bold shadow-sm border flex-shrink-0 text-center
        ${hideUnit ? 'rounded-full h-10 min-w-[2.75rem] px-4 text-base' : allowFractions ? 'rounded-lg px-2.5 py-1 min-w-[3rem] min-h-8 text-sm' : 'rounded-lg h-8 px-3 text-sm'}
        ${bgClass ?? ''}
        ${isOpen ? `ring-2 ${isMeat ? 'ring-rose-200 dark:ring-rose-800' : 'ring-brand-sage/40'}` : ''}
        ${isMeat
            ? 'bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-900'
            : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-700'}
    `;

    const renderTriggerLabel = () => {
        if (allowFractions && !hideUnit) {
            return (
                <span className="inline-flex items-center justify-center gap-1.5" dir={rtl ? 'rtl' : 'ltr'}>
                    <CookingQuantityLabel
                        quantity={value}
                        unit={unit}
                        t={(k) => t(k as Parameters<typeof getTranslation>[1])}
                        rtl={rtl}
                        isMeat={isMeat}
                    />
                </span>
            );
        }

        // Isolate quantity as LTR so values don't scramble under RTL parents.
        // Vulgar glyphs (5½) avoid the broken "5 1 / 2" slash rendering in cart/list pills.
        return (
            <span className="inline-flex items-center justify-center gap-1.5" dir={rtl ? 'rtl' : 'ltr'}>
                {!hideQuantity && (
                    <bdi dir="ltr" className="font-mono tabular-nums whitespace-nowrap">
                        {formatQuantityVulgarDisplay(value)}
                    </bdi>
                )}
                {!hideUnit && !!unit && (
                    <span className="opacity-60 text-xs uppercase tracking-tight">
                        {translateUnitLabel(unit, (key) => t(key as Parameters<typeof getTranslation>[1]))}
                    </span>
                )}
            </span>
        );
    };

    const editorPanel = (
        <div
            ref={editorRef}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className={`flex flex-col items-start gap-1.5 animate-fade-in flex-shrink-0 ${floatWhenOpen ? '' : 'relative'}`}
            style={floatWhenOpen ? popoverStyle : { zIndex: floatingZIndex }}
        >
            <div
                className={`flex items-center ${hideUnit ? 'h-10' : 'h-9'} bg-white dark:bg-slate-900 border ${activeBorderColor} rounded-full shadow-lg overflow-hidden relative w-fit ring-4 ${ringColor}`}
                dir="ltr"
            >
                {!hideQuantity && (
                    <button
                        type="button"
                        onPointerDown={handleStepButtonPointerDown}
                        onClick={(e) => handleStepChange(e, value - step)}
                        className={`w-9 h-full flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-r border-slate-100 dark:border-slate-800 ${btnColor}`}
                    >
                        <MinusIcon className="w-4 h-4" />
                    </button>
                )}

                {!hideQuantity && (
                    <input
                        ref={inputRef}
                        type="text"
                        inputMode="decimal"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onFocus={selectAllInput}
                        onClick={(e) => {
                            e.stopPropagation();
                            selectAllInput();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                closeEditor();
                            }
                        }}
                        className="min-w-[3.5rem] max-w-[5.5rem] h-full text-center bg-transparent border-none focus:ring-0 px-1.5 py-0 text-base font-bold text-slate-800 dark:text-slate-200 tabular-nums appearance-none"
                    />
                )}

                {!hideQuantity && (
                    <button
                        type="button"
                        onPointerDown={handleStepButtonPointerDown}
                        onClick={(e) => handleStepChange(e, value + step)}
                        className={`w-9 h-full flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-l border-r border-slate-100 dark:border-slate-800 ${btnColor}`}
                    >
                        <PlusIcon className="w-4 h-4" />
                    </button>
                )}

                {!hideUnit && (
                    <div className="h-full flex items-center justify-center px-2 bg-slate-50 dark:bg-slate-800 min-w-[50px] relative">
                        {showUnitSelector ? (
                            <div className="relative flex items-center">
                                <select
                                    value={canonicalUnit}
                                    onChange={(e) => handleUpdateUnit(e.target.value)}
                                    className="appearance-none bg-transparent border-none text-xs font-bold text-slate-600 dark:text-slate-400 py-0 pl-1 pr-4 focus:ring-0 cursor-pointer outline-none w-full text-right"
                                >
                                    {effectiveUnits.map(u => (
                                        <option key={u} value={u} className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900">
                                            {translateUnitLabel(u, (key) => t(key as Parameters<typeof getTranslation>[1]))}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDownIcon className="w-3 h-3 text-slate-400 absolute right-0 pointer-events-none" />
                            </div>
                        ) : (
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-400 whitespace-nowrap px-1">
                                {translateUnitLabel(canonicalUnit, (key) => t(key as Parameters<typeof getTranslation>[1]))}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {!hideQuantity && showFractionShortcuts && (
                <div className="flex items-center gap-1.5 px-0.5 py-0.5 bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-800" dir="ltr">
                    {DISPLAY_FRACTIONS.map((fraction) => (
                        <button
                            key={fraction.label}
                            type="button"
                            onPointerDown={handleStepButtonPointerDown}
                            onClick={(e) => handleFractionChange(e, fraction.value)}
                            className={`min-w-[2.35rem] px-2.5 py-1.5 rounded-full text-sm font-black leading-none border transition-colors ${
                                Math.abs(value - fraction.value) < 0.02
                                    ? 'bg-brand-sage text-white border-brand-sage'
                                    : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-brand-sage/50'
                            }`}
                        >
                            {fraction.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <>
            {/* Inline mode (carts/lists): replace trigger with editor. Float mode: keep trigger as anchor. */}
            {(!isOpen || floatWhenOpen) && (
                <button
                    ref={anchorRef}
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isOpen) {
                            closeEditor();
                        } else {
                            openEditor();
                        }
                    }}
                    className={triggerClassName}
                >
                    {renderTriggerLabel()}
                </button>
            )}

            {isOpen && (
                floatWhenOpen
                    ? createPortal(editorPanel, document.body)
                    : (
                        <>
                            <div
                                className="fixed inset-0 cursor-default"
                                style={{ zIndex }}
                                onPointerDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    closeEditorWithoutClickThrough();
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            />
                            {editorPanel}
                        </>
                    )
            )}
        </>
    );
};

export default SmartUnitCapsule;
